import { renderStoreItems } from './script.js';
import { musicPlayer } from './musicConfig.js';
import { fetchCallReadOnlyFunction, principalCV } from '@stacks/transactions';
import { STACKS_MAINNET } from '@stacks/network';
import loader from './loader.js';

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatDuration(sec) {
  if (sec == null || sec === '' || Number.isNaN(Number(sec))) return '';
  const n = Math.floor(Number(sec));
  const m = Math.floor(n / 60);
  const r = n % 60;
  return `${m}:${r.toString().padStart(2, '0')}`;
}

async function postAnalytics(event, creator, slug, wallet) {
  try {
    await fetch('/api/album/analytics', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event,
        creator_slug: creator,
        album_slug: slug,
        wallet: wallet || undefined
      })
    });
  } catch (e) {
    console.warn('Analytics failed', e);
  }
}

async function fetchHoldings(principal) {
  const url = `/api/proxy/stacks?principal=${encodeURIComponent(principal)}&limit=200`;
  const res = await fetch(url);
  if (!res.ok) return [];
  const data = await res.json();
  return data.results || [];
}

function hasAlbumNft(holdings, contractAddress, contractName, assetName) {
  if (!contractAddress || !contractName) return false;
  const needle = `${contractAddress}.${contractName}::${assetName}`.toLowerCase();
  return holdings.some((h) => (h.asset_identifier || '').toLowerCase() === needle);
}

async function readMintCount(contractAddress, contractName) {
  if (!contractAddress || !contractName) return null;
  try {
    const result = await fetchCallReadOnlyFunction({
      contractAddress,
      contractName,
      functionName: 'get-mint-count',
      functionArgs: [],
      network: STACKS_MAINNET,
      senderAddress: contractAddress
    });
    if (result?.value !== undefined && result?.value !== null) {
      return Number(result.value);
    }
  } catch (e) {
    console.warn('get-mint-count failed', e);
  }
  return null;
}

async function readHasMinted(contractAddress, contractName, address) {
  if (!contractAddress || !contractName || !address) return false;
  try {
    const result = await fetchCallReadOnlyFunction({
      contractAddress,
      contractName,
      functionName: 'has-minted',
      functionArgs: [principalCV(address)],
      network: STACKS_MAINNET,
      senderAddress: address
    });
    if (result?.value?.type === 'bool') {
      return result.value.value === true;
    }
  } catch (e) {
    console.warn('has-minted failed', e);
  }
  return false;
}

function updateDocumentMeta(title, description, imageUrl, canonicalUrl) {
  document.title = title || 'Gated';
  const setMeta = (sel, attr, val) => {
    let el = document.querySelector(sel);
    if (!el) {
      el = document.createElement('meta');
      if (sel.startsWith('meta[property')) {
        el.setAttribute('property', sel.match(/property="([^"]+)"/)[1]);
      } else if (sel.startsWith('meta[name')) {
        el.setAttribute('name', sel.match(/name="([^"]+)"/)[1]);
      }
      document.head.appendChild(el);
    }
    el.setAttribute(attr, val);
  };
  if (description) {
    setMeta('meta[name="description"]', 'content', description);
    setMeta('meta[property="og:description"]', 'content', description);
    setMeta('meta[name="twitter:description"]', 'content', description);
  }
  if (imageUrl) {
    setMeta('meta[property="og:image"]', 'content', imageUrl);
    setMeta('meta[name="twitter:image"]', 'content', imageUrl);
  }
  setMeta('meta[property="og:title"]', 'content', title);
  setMeta('meta[name="twitter:title"]', 'content', title);
  setMeta('meta[property="og:url"]', 'content', canonicalUrl);
  setMeta('meta[name="twitter:url"]', 'content', canonicalUrl);
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', canonicalUrl);
}

/**
 * @param {{ album: object, tracks: object[] }} data - Same shape as GET /api/album/public/...
 * @param {{ previewMode?: boolean, mountEl?: HTMLElement }} options - preview skips analytics / on-chain reads
 */
export async function renderAlbumPageFromData(router, data, creatorSlug, albumSlug, options = {}) {
  const { previewMode = false, mountEl = null } = options;
  const root = mountEl || router.mainContent;

  const canonicalUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}/${creatorSlug}/${albumSlug}`
      : '';

  try {
    const { album, tracks } = data;
    const cover = album.cover_url || '';
    const desc = album.description || '';

    updateDocumentMeta(
      album.title || 'Album',
      desc,
      cover,
      canonicalUrl
    );

    if (!previewMode) {
      postAnalytics('visit', creatorSlug, albumSlug);
    }

    const musicItem = {
      title: album.title,
      subtitle: 'Founding Collector edition',
      albumImage: cover,
      description: desc,
      isMusicPlayer: true,
      albumPrice: 0,
      albumSupply: album.max_supply,
      albumCoverArtist: '',
      songs: (tracks || []).map((t, i) => ({
        songArtist: album.title,
        songName: t.title || `Track ${i + 1}`,
        duration: formatDuration(t.duration_seconds) || '—',
        audioUrl: t.audio_url,
        imgUrl: t.art_url || cover,
        songPrice: '0',
        imgArtist: '',
        songSupply: album.max_supply
      }))
    };

    if (!musicItem.songs.length) {
      root.innerHTML = `
        <div class="album-page-error" style="padding:2rem;">
          <h1>${escapeHtml(album.title)}</h1>
          <p>No tracks published yet.</p>
        </div>`;
      if (!mountEl) loader.hide();
      return;
    }

    let mintCountDisplay = album.mint_count ?? 0;
    if (!previewMode && album.contract_address && album.contract_name) {
      const onChainMint = await readMintCount(album.contract_address, album.contract_name);
      mintCountDisplay = onChainMint != null ? onChainMint : album.mint_count ?? 0;
    }

    let viewerAddress = null;
    let isHolder = false;
    if (!previewMode && router.auth?.userSession?.isUserSignedIn()) {
      const ud = router.auth.userSession.loadUserData();
      viewerAddress = ud?.profile?.stxAddress?.mainnet || null;
      if (viewerAddress) {
        postAnalytics('wallet_connect', creatorSlug, albumSlug, viewerAddress);
        const holdings = await fetchHoldings(viewerAddress);
        isHolder = hasAlbumNft(
          holdings,
          album.contract_address,
          album.contract_name,
          album.nft_asset_name || 'founding-album-pass'
        );
        if (!isHolder && album.contract_address && album.contract_name) {
          isHolder = await readHasMinted(
            album.contract_address,
            album.contract_name,
            viewerAddress
          );
        }
      }
    }

    const noteHtml = album.collector_note
      ? `<div class="collectors-note-inner">${escapeHtml(album.collector_note).replace(/\n/g, '<br>')}</div>`
      : '<p class="collectors-note-empty">No note yet.</p>';

    const previewBanner = previewMode
      ? `<div class="album-preview-banner">Preview — not published yet</div>`
      : '';

    root.innerHTML = `
      <div class="public-album-page" data-creator="${escapeHtml(creatorSlug)}" data-slug="${escapeHtml(albumSlug)}">
        ${previewBanner}
        <div class="public-album-hero">
          <img class="public-album-cover" src="${escapeHtml(cover)}" alt="" width="320" height="320" loading="eager" />
          <div class="public-album-hero-text">
            <p class="public-album-badge">Founding Collector</p>
            <h1 class="public-album-title">${escapeHtml(album.title)}</h1>
            <p class="public-album-desc">${escapeHtml(desc)}</p>
            <p class="public-album-gas-note">Collecting is free; you still pay a small STX network fee in your wallet.</p>
            <p class="public-album-mint-stats" id="album-mint-stats">${mintCountDisplay} / ${album.max_supply} collected</p>
            <div id="album-collect-slot" class="album-collect-slot"></div>
          </div>
        </div>
        <div id="store-container"></div>
        <section class="collectors-note-section" aria-label="Collector content">
          <div class="collectors-note-teaser" id="collectors-teaser" style="display:${isHolder ? 'none' : 'block'}">
            <div class="collectors-note-blur">
              <p><strong>Collector’s note</strong> — unlock by collecting this album.</p>
            </div>
            <button type="button" class="collectors-note-cta" id="collectors-cta">Collect to unlock</button>
          </div>
          <div class="collectors-note-reveal" id="collectors-reveal" style="display:${isHolder ? 'block' : 'none'}">
            <h2>Collector’s note</h2>
            ${noteHtml}
          </div>
        </section>
      </div>
    `;

    const mintStatsEl = document.getElementById('album-mint-stats');

    renderStoreItems([musicItem], { albumId: mintCountDisplay, trackIds: [] }, {
      albumProduct: true,
      alreadyMinted: isHolder,
      mintCountEl: mintStatsEl,
      albumMeta: {
        creatorSlug,
        albumSlug,
        contractAddress: album.contract_address,
        contractName: album.contract_name,
        maxSupply: album.max_supply,
        onMintConfirmed: async () => {
          if (!previewMode) {
            postAnalytics('mint', creatorSlug, albumSlug, viewerAddress);
          }
          const el = document.getElementById('collectors-teaser');
          const rev = document.getElementById('collectors-reveal');
          if (el) el.style.display = 'none';
          if (rev) rev.style.display = 'block';
          const confetti = document.createElement('div');
          confetti.className = 'album-mint-celebration';
          confetti.textContent = 'Welcome, Founding Collector';
          document.querySelector('.public-album-page')?.appendChild(confetti);
          setTimeout(() => confetti.remove(), 4000);
        }
      }
    });

    document.getElementById('collectors-cta')?.addEventListener('click', () => {
      document.querySelector('.buy-album-button')?.click();
    });

    if (!window.musicPlayer?.isInitialized) {
      window.musicPlayer = Object.create(musicPlayer);
      window.musicPlayer.audioPlayer = new Audio();
      window.musicPlayer.init();
    }
    window.musicPlayer.auth = router.auth;
    window.__albumPlaylistItem = musicItem;
    window.musicPlayer.loadTrack(0, musicItem, isHolder);
    router.setupMusicEventListeners();

    const sticky = document.getElementById('music-player-sticky');
    if (sticky) sticky.style.display = 'none';

    if (!mountEl) {
      loader.hide();
    }
  } catch (e) {
    console.error(e);
    root.innerHTML = `<div class="album-page-error" style="padding:2rem;">Failed to load album.</div>`;
    if (!mountEl) {
      loader.hide();
    }
  }
}

export async function renderPublicAlbumPage(router, creatorSlug, albumSlug) {
  loader.show();
  loader.updateText('Loading album…');

  try {
    const res = await fetch(
      `/api/album/public/${encodeURIComponent(creatorSlug)}/${encodeURIComponent(albumSlug)}`
    );
    const raw = await res.text();
    let data;
    try {
      data = JSON.parse(raw);
    } catch {
      throw new Error('Invalid album response');
    }

    if (!res.ok) {
      router.mainContent.innerHTML = `
        <div class="album-page-error" style="padding:2rem;text-align:center;">
          <h1>Album not found</h1>
          <p>${escapeHtml(data.error || 'This album is unavailable.')}</p>
        </div>`;
      loader.hide();
      return;
    }

    await renderAlbumPageFromData(router, data, creatorSlug, albumSlug, {});
  } catch (e) {
    console.error(e);
    router.mainContent.innerHTML = `<div class="album-page-error" style="padding:2rem;">Failed to load album.</div>`;
    loader.hide();
  }
}
